import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Title,
  Card,
  Stack,
  Group,
  Text,
  Badge,
  Button,
  Table,
  Select,
  Grid,
  Alert,
  Loader,
  Paper,
  Textarea,
  ActionIcon,
  Timeline,
} from '@mantine/core';
import { IconArrowLeft, IconCheck, IconTrash, IconMessage, IconCircleCheck, IconPackage, IconTruck, IconShoppingBag, IconMail, IconFileInvoice } from '@tabler/icons-react';
import { getOrderById, updateOrderStatus, updateOrderPaymentStatus, addOrderComment, deleteOrderComment, createOrderInvoice, getOrderInvoicePdf } from '@/lib/admin/orders';
import { Order } from '@/types';
import { notifications } from '@mantine/notifications';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/admin/AdminLayout';

const PAYMENT_STATUS_COLORS: Record<Order['payment']['status'], string> = {
  pending: 'yellow',
  succeeded: 'green',
  failed: 'red',
};

export default function AdminOrderDetailPage() {
  const navigate = useNavigate();
  const params = useParams();
  const { user, isAdmin: isAdminUser, loading: authLoading } = useAuth();
  const orderId = params?.id as string;
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState<any[]>([]);
  const [creatingInvoice, setCreatingInvoice] = useState(false);

  useEffect(() => {
    if (!authLoading && (!user || !isAdminUser)) {
      navigate('/xyz/login');
      return;
    }

    if (orderId && user && isAdminUser) {
      loadOrder();
    }
  }, [orderId, user, isAdminUser, authLoading, navigate]);

  const loadOrder = async () => {
    if (!orderId) return;
    
    setLoading(true);
    try {
      const fetchedOrder = await getOrderById(orderId);
      setOrder(fetchedOrder);
      // Extract email notifications if present
      if (fetchedOrder && (fetchedOrder as any).emailNotifications) {
        setEmailNotifications((fetchedOrder as any).emailNotifications);
      }
    } catch (err: any) {
      notifications.show({
        title: 'Error',
        message: err.message || 'Failed to load order',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus: Order['status']) => {
    if (!order) return;
    
    setUpdating(true);
    try {
      await updateOrderStatus(order.id!, newStatus);
      setOrder({ ...order, status: newStatus });
      // Reload order to get updated email notifications
      await loadOrder();
      notifications.show({
        title: 'Success',
        message: 'Order status updated',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch (err: any) {
      notifications.show({
        title: 'Error',
        message: err.message || 'Failed to update order status',
        color: 'red',
      });
    } finally {
      setUpdating(false);
    }
  };

  const handlePaymentStatusUpdate = async (newStatus: Order['payment']['status']) => {
    if (!order) return;
    
    setUpdating(true);
    try {
      await updateOrderPaymentStatus(order.id!, newStatus);
      setOrder({ ...order, payment: { ...order.payment, status: newStatus } });
      notifications.show({
        title: 'Success',
        message: 'Payment status updated',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch (err: any) {
      notifications.show({
        title: 'Error',
        message: err.message || 'Failed to update payment status',
        color: 'red',
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleAddComment = async () => {
    if (!order || !newComment.trim() || !user) return;

    setAddingComment(true);
    try {
      const createdBy = user.email || user.uid;
      await addOrderComment(order.id!, newComment.trim(), createdBy);
      await loadOrder(); // Reload order to get updated comments
      setNewComment('');
      notifications.show({
        title: 'Success',
        message: 'Comment added',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch (err: any) {
      notifications.show({
        title: 'Error',
        message: err.message || 'Failed to add comment',
        color: 'red',
      });
    } finally {
      setAddingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!order) return;

    setUpdating(true);
    try {
      await deleteOrderComment(order.id!, commentId);
      await loadOrder(); // Reload order to get updated comments
      notifications.show({
        title: 'Success',
        message: 'Comment deleted',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch (err: any) {
      notifications.show({
        title: 'Error',
        message: err.message || 'Failed to delete comment',
        color: 'red',
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleCreateInvoice = async () => {
    if (!order) return;

    setCreatingInvoice(true);
    try {
      await createOrderInvoice(order.id!);
      await loadOrder(); // Reload order to get updated invoice ID and timeline
      notifications.show({
        title: 'Success',
        message: 'Invoice created successfully',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch (err: any) {
      notifications.show({
        title: 'Error',
        message: err.message || 'Failed to create invoice',
        color: 'red',
      });
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleViewInvoice = async () => {
    if (!order || !order.payment?.stripeInvoiceId) return;

    try {
      const pdfUrl = await getOrderInvoicePdf(order.id!);
      window.open(pdfUrl, '_blank');
    } catch (err: any) {
      notifications.show({
        title: 'Error',
        message: err.message || 'Failed to open invoice',
        color: 'red',
      });
    }
  };

  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    try {
      let d: Date;
      
      // Handle Firestore Timestamp
      if (date && typeof date === 'object' && 'toDate' in date) {
        d = date.toDate();
      } 
      // Handle ISO string
      else if (typeof date === 'string') {
        d = new Date(date);
      }
      // Handle Date object
      else if (date instanceof Date) {
        d = date;
      }
      // Handle Firestore Timestamp with seconds/nanoseconds
      else if (date && typeof date === 'object' && ('seconds' in date || 'nanoseconds' in date)) {
        d = new Date(date.seconds * 1000 + (date.nanoseconds || 0) / 1000000);
      }
      else {
        d = new Date(date);
      }
      
      if (isNaN(d.getTime())) {
        return 'Invalid date';
      }
      
      return new Intl.DateTimeFormat('pl-PL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(d);
    } catch {
      return 'Invalid date';
    }
  };

  if (authLoading || loading) {
    return (
      <AdminLayout>
        <Container size="xl" py="xl">
          <Loader size="lg" style={{ display: 'block', margin: '0 auto' }} />
        </Container>
      </AdminLayout>
    );
  }

  if (!user || !isAdminUser) {
    return null;
  }

  if (!order) {
    return (
      <AdminLayout>
        <Container size="xl" py="xl">
          <Alert color="red" title="Order not found">
            <Button leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/xyz/orders')} mt="md">
              Back to Orders
            </Button>
          </Alert>
        </Container>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Container size="xl" py="xl">
        <Stack gap="md">
          <Group>
            <Button
              leftSection={<IconArrowLeft size={16} />}
              variant="subtle"
              onClick={() => navigate('/xyz/orders')}
            >
              Back to Orders
            </Button>
          </Group>

          <Group justify="space-between">
            <div>
              <Title order={1}>Order #{order.id?.substring(0, 8)}</Title>
              <Text c="dimmed" size="sm">Created: {formatDate(order.createdAt)}</Text>
            </div>
            <Group>
              <Select
                label="Order Status"
                value={order.status}
                onChange={(value) => value && handleStatusUpdate(value as Order['status'])}
                data={[
                  { value: 'pending', label: 'Pending' },
                  { value: 'processing', label: 'Processing' },
                  { value: 'shipped', label: 'Shipped' },
                  { value: 'delivered', label: 'Delivered' },
                ]}
                disabled={updating}
              />
              <Select
                label="Payment Status"
                value={order.payment.status}
                onChange={(value) => value && handlePaymentStatusUpdate(value as Order['payment']['status'])}
                data={[
                  { value: 'pending', label: 'Pending' },
                  { value: 'succeeded', label: 'Succeeded' },
                  { value: 'failed', label: 'Failed' },
                ]}
                disabled={updating}
              />
            </Group>
          </Group>

          <Grid>
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Card withBorder padding="md" radius="md">
                <Title order={3} mb="md">Order Items</Title>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Product</Table.Th>
                      <Table.Th>Quantity</Table.Th>
                      <Table.Th>Price</Table.Th>
                      <Table.Th>Total</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {order.items.map((item, index) => (
                      <Table.Tr key={index}>
                        <Table.Td>
                          <Group gap="sm">
                            {item.product.images && item.product.images.length > 0 && (
                              <div style={{ width: '100px', height: '100px', overflow: 'hidden', borderRadius: '4px' }}>
                                <img
                                  src={item.product.images[0]}
                                  alt={item.product.name}
                                  style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                />
                              </div>
                            )}
                            <div>
                              <Text fw={500}>{item.product.name}</Text>
                              <Text size="xs" c="dimmed">
                                {item.product.price.toFixed(2)} PLN each
                              </Text>
                            </div>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Text>{item.quantity}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text>{item.product.price.toFixed(2)} PLN</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text fw={600}>
                            {(item.product.price * item.quantity).toFixed(2)} PLN
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                  <Table.Tfoot>
                    <Table.Tr>
                      <Table.Td colSpan={3}>
                        <Text fw={600}>Subtotal</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text fw={600}>{order.subtotal?.toFixed(2) || order.items.reduce((sum, item) => sum + item.product.price * item.quantity, 0).toFixed(2)} PLN</Text>
                      </Table.Td>
                    </Table.Tr>
                    {order.shipping?.cost !== undefined && (
                      <Table.Tr>
                        <Table.Td colSpan={3}>
                          <Text fw={600}>Shipping</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text fw={600}>{order.shipping.cost.toFixed(2)} PLN</Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                    {order.vat !== undefined && (
                      <Table.Tr>
                        <Table.Td colSpan={3}>
                          <Text fw={600} c="dimmed">w tym VAT (23%)</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text fw={600} c="dimmed">{order.vat.toFixed(2)} PLN</Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                    <Table.Tr>
                      <Table.Td colSpan={3}>
                        <Text fw={700} size="lg">Total</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text fw={700} size="lg">{order.total.toFixed(2)} PLN</Text>
                      </Table.Td>
                    </Table.Tr>
                  </Table.Tfoot>
                </Table>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 4 }}>
              <Stack gap="md">
                <Card withBorder padding="md" radius="md">
                  <Title order={4} mb="md">Customer Information</Title>
                  <Stack gap="xs">
                    <div>
                      <Text size="xs" c="dimmed">Name</Text>
                      <Text fw={500}>{order.customer.name}</Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">Email</Text>
                      <Text fw={500}>{order.customer.email}</Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">Phone</Text>
                      <Text fw={500}>{order.customer.phone}</Text>
                    </div>
                  </Stack>
                </Card>

                <Card withBorder padding="md" radius="md">
                  <Title order={4} mb="md">Shipping Information</Title>
                  <Stack gap="xs">
                    <div>
                      <Text size="xs" c="dimmed">Method</Text>
                      <Text fw={500}>{order.shipping.method === 'inpost' ? 'InPost Parcel Locker' : 'Courier Delivery'}</Text>
                    </div>
                    {order.shipping.cost !== undefined && (
                      <div>
                        <Text size="xs" c="dimmed">Shipping Cost</Text>
                        <Text fw={500}>{order.shipping.cost.toFixed(2)} PLN</Text>
                      </div>
                    )}
                    {order.shipping.method === 'inpost' && order.shipping.paczkomat && (
                      <>
                        <div>
                          <Text size="xs" c="dimmed">Paczkomat</Text>
                          <Text fw={500}>{order.shipping.paczkomat.name}</Text>
                        </div>
                        <div>
                          <Text size="xs" c="dimmed">Address</Text>
                          <Text fw={500}>
                            {order.shipping.paczkomat.address}
                            <br />
                            {order.shipping.paczkomat.city} {order.shipping.paczkomat.postCode}
                          </Text>
                        </div>
                        <div>
                          <Text size="xs" c="dimmed">Location ID</Text>
                          <Text fw={500} style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                            {order.shipping.paczkomat.locationId}
                          </Text>
                        </div>
                      </>
                    )}
                    {order.shipping.method === 'courier' && order.shipping.address && (
                      <>
                        <div>
                          <Text size="xs" c="dimmed">Recipient Name</Text>
                          <Text fw={500}>{order.shipping.address.name}</Text>
                        </div>
                        {order.shipping.address.companyName && (
                          <div>
                            <Text size="xs" c="dimmed">Company Name</Text>
                            <Text fw={500}>{order.shipping.address.companyName}</Text>
                          </div>
                        )}
                        <div>
                          <Text size="xs" c="dimmed">Address</Text>
                          <Text fw={500}>
                            {order.shipping.address.street}
                            <br />
                            {order.shipping.address.city} {order.shipping.address.postCode}
                          </Text>
                        </div>
                      </>
                    )}
                  </Stack>
                </Card>

                {order.vatInvoice && (
                  <Card withBorder padding="md" radius="md">
                    <Title order={4} mb="md">VAT Invoice Information</Title>
                    <Stack gap="xs">
                      <div>
                        <Text size="xs" c="dimmed">Company Name</Text>
                        <Text fw={500}>{order.vatInvoice.companyName}</Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">Tax ID (NIP)</Text>
                        <Text fw={500}>{order.vatInvoice.taxId}</Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">Company Address</Text>
                        <Text fw={500}>{order.vatInvoice.address}</Text>
                      </div>
                    </Stack>
                  </Card>
                )}

                <Card withBorder padding="md" radius="md">
                  <Title order={4} mb="md">Payment Information</Title>
                  <Stack gap="xs">
                    <div>
                      <Text size="xs" c="dimmed">Status</Text>
                      <Badge color={PAYMENT_STATUS_COLORS[order.payment.status]} variant="light">
                        {order.payment.status}
                      </Badge>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">Stripe Payment Intent</Text>
                      <Text fw={500} style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        {order.payment.stripePaymentIntentId}
                      </Text>
                    </div>
                    {order.payment.stripeInvoiceId && (
                      <div>
                        <Text size="xs" c="dimmed">Invoice ID</Text>
                        <Text fw={500} style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                          {order.payment.stripeInvoiceId}
                        </Text>
                      </div>
                    )}
                    {order.payment && order.payment.status?.toLowerCase() === 'succeeded' && (
                      <Group mt="md">
                        {!order.payment.stripeInvoiceId ? (
                          <Button
                            leftSection={<IconFileInvoice size={16} />}
                            onClick={handleCreateInvoice}
                            disabled={creatingInvoice}
                            loading={creatingInvoice}
                            variant="light"
                          >
                            Create Invoice
                          </Button>
                        ) : (
                          <Button
                            leftSection={<IconFileInvoice size={16} />}
                            onClick={handleViewInvoice}
                            variant="light"
                            color="blue"
                          >
                            View Invoice PDF
                          </Button>
                        )}
                      </Group>
                    )}
                  </Stack>
                </Card>

                <Card withBorder padding="md" radius="md">
                  <Title order={4} mb="md">Internal Comments</Title>
                  <Stack gap="md">
                    <Textarea
                      placeholder="Add an internal comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      rows={3}
                    />
                    <Button
                      leftSection={<IconMessage size={16} />}
                      onClick={handleAddComment}
                      disabled={!newComment.trim() || addingComment}
                      loading={addingComment}
                      size="sm"
                    >
                      Add Comment
                    </Button>

                    {order.comments && order.comments.length > 0 && (
                      <Stack gap="xs" mt="md">
                        {order.comments.map((comment) => (
                          <Paper key={comment.id} p="sm" withBorder radius="sm">
                            <Group justify="space-between" mb="xs">
                              <Text size="xs" c="dimmed">
                                {comment.createdBy} • {formatDate(comment.createdAt)}
                              </Text>
                              <ActionIcon
                                size="xs"
                                color="red"
                                variant="subtle"
                                onClick={() => handleDeleteComment(comment.id)}
                                disabled={updating}
                              >
                                <IconTrash size={14} />
                              </ActionIcon>
                            </Group>
                            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                              {comment.text}
                            </Text>
                          </Paper>
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </Card>

                <Card withBorder padding="md" radius="md">
                  <Title order={4} mb="md">Order Timeline</Title>
                  {order.timeline && order.timeline.length > 0 ? (
                    <Timeline active={order.timeline.length - 1} bulletSize={24} lineWidth={2}>
                      {order.timeline.map((event) => {
                        let icon;
                        let color;
                        
                        switch (event.type) {
                          case 'created':
                            icon = <IconShoppingBag size={16} />;
                            color = 'blue';
                            break;
                          case 'payment_received':
                            icon = <IconCheck size={16} />;
                            color = 'green';
                            break;
                          case 'packed':
                            icon = <IconPackage size={16} />;
                            color = 'cyan';
                            break;
                          case 'shipped':
                            icon = <IconTruck size={16} />;
                            color = 'orange';
                            break;
                          case 'delivered':
                            icon = <IconCircleCheck size={16} />;
                            color = 'green';
                            break;
                          case 'invoice_created':
                            icon = <IconFileInvoice size={16} />;
                            color = 'blue';
                            break;
                          default:
                            icon = <IconCheck size={16} />;
                            color = 'gray';
                        }
                        
                        return (
                          <Timeline.Item
                            key={event.id}
                            bullet={icon}
                            title={event.description}
                            color={color}
                          >
                            <Text size="xs" c="dimmed" mt={4}>
                              {formatDate(event.createdAt)}
                              {event.createdBy && ` • ${event.createdBy}`}
                            </Text>
                          </Timeline.Item>
                        );
                      })}
                    </Timeline>
                  ) : (
                    <Text size="sm" c="dimmed">No timeline events yet</Text>
                  )}
                </Card>

                <Card withBorder padding="md" radius="md">
                  <Title order={4} mb="md">Email Notifications</Title>
                  {emailNotifications && emailNotifications.length > 0 ? (
                    <Stack gap="md">
                      {emailNotifications.map((notification, index) => (
                        <Paper key={notification.id || index} p="sm" withBorder radius="sm">
                          <Group justify="space-between" mb="xs">
                            <Group gap="xs">
                              <IconMail size={16} />
                              <Text fw={500} size="sm">
                                {notification.type.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                              </Text>
                              <Badge 
                                color={notification.status === 'sent' ? 'green' : 'red'} 
                                variant="light"
                                size="sm"
                              >
                                {notification.status}
                              </Badge>
                            </Group>
                            <Text size="xs" c="dimmed">
                              {formatDate(notification.sentAt)}
                            </Text>
                          </Group>
                          <Text size="xs" c="dimmed" mb="xs">
                            To: {notification.to}
                          </Text>
                          <Text size="xs" fw={500} mb="xs">
                            Subject: {notification.subject}
                          </Text>
                          {notification.error && (
                            <Alert color="red" mt="xs">
                              Error: {notification.error}
                            </Alert>
                          )}
                          <details style={{ marginTop: '8px' }}>
                            <summary style={{ cursor: 'pointer', fontSize: '12px', color: '#666' }}>
                              View Email Content
                            </summary>
                            <div 
                              style={{ 
                                marginTop: '8px', 
                                padding: '8px', 
                                backgroundColor: '#f8f9fa', 
                                borderRadius: '4px',
                                fontSize: '12px',
                                maxHeight: '200px',
                                overflow: 'auto'
                              }}
                              dangerouslySetInnerHTML={{ __html: notification.htmlContent }}
                            />
                          </details>
                        </Paper>
                      ))}
                    </Stack>
                  ) : (
                    <Text size="sm" c="dimmed">No email notifications sent yet</Text>
                  )}
                </Card>
              </Stack>
            </Grid.Col>
          </Grid>
        </Stack>
      </Container>
    </AdminLayout>
  );
}
